import type * as React from "react";

import { isValid } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { fromParts } from "../dateAdapter";
import { invoke } from "../lib/utils";
import MonthPicker, {
  type MonthPickerOnChangeData,
} from "../pickers/monthPicker/MonthPicker";
import InputView from "../views/InputView";
import BaseInput, {
  type BaseInputProps,
  type BaseInputState,
  type DateRelatedProps,
  type DisableValuesProps,
  type MinMaxValueProps,
} from "./BaseInput";
import {
  buildValue,
  dateValueToString,
  parseArrayOrValue,
  parseValue,
} from "./parse";

export type MonthInputProps = BaseInputProps &
  DateRelatedProps &
  DisableValuesProps &
  MinMaxValueProps;

export type MonthInputOnChangeData = MonthInputProps;

class MonthInput extends BaseInput<MonthInputProps, BaseInputState> {
  public static readonly defaultProps = {
    ...BaseInput.defaultProps,
    dateFormat: "MMM",
    icon: CalendarIcon,
  };

  constructor(props: MonthInputProps) {
    super(props);
    this.state = { popupIsClosed: true };
  }

  public render() {
    const {
      value,
      dateFormat,
      initialDate,
      disable,
      maxDate,
      minDate,
      closable,
      localization,
      ...rest
    } = this.props;

    return (
      <InputView
        popupIsClosed={this.state.popupIsClosed}
        {...(rest as any)}
        value={dateValueToString(value, dateFormat, localization)}
        onMount={this.onInputViewMount}
        closePopup={this.closePopup}
        openPopup={this.openPopup}
        renderPicker={this.getPicker}
      />
    );
  }

  private getPicker = () => {
    const {
      value,
      dateFormat,
      disable,
      maxDate,
      minDate,
      localization,
      initialDate,
    } = this.props;

    return (
      <MonthPicker
        inline={this.props.inline}
        isPickerInFocus={this.isPickerInFocus}
        isTriggerInFocus={this.isTriggerInFocus}
        onCalendarViewMount={this.onCalendarViewMount}
        closePopup={this.closePopup}
        hasHeader={false}
        onChange={this.handleSelect as any}
        initializeWith={buildValue(
          value,
          initialDate,
          localization,
          dateFormat,
        )}
        value={buildValue(value, null, localization, dateFormat, null)}
        disable={parseArrayOrValue(disable, dateFormat, localization)}
        maxDate={parseValue(maxDate, dateFormat, localization)}
        minDate={parseValue(minDate, dateFormat, localization)}
        localization={localization}
        onHeaderClick={() => undefined}
      />
    );
  };

  private handleSelect = (
    e: React.SyntheticEvent<HTMLElement> | null,
    { value }: MonthPickerOnChangeData,
  ) => {
    const { localization } = this.props;
    const date = localization
      ? fromParts({ month: value.month }, localization)
      : fromParts({ month: value.month });
    let output = "";
    if (isValid(date.toDate())) {
      output = date.format(this.props.dateFormat!);
    }
    invoke(this.props, "onChange", e, { ...this.props, value: output });
    if (this.props.closable) {
      this.closePopup();
    }
  };
}

export default MonthInput;
